// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Settings Page
 *
 * Manages site configuration and portfolio settings.
 */

import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  ArrowLeft,
  Settings,
  Globe,
  Search,
  Bell,
  Mail,
  Shield,
  Loader2,
  Save,
} from 'lucide-react'
import { useSiteConfig, useUpdateSiteConfig } from '../hooks'
import { AgentTokensCard } from '../components/AgentTokensCard'

// =============================================================================
// Form Schema
// =============================================================================

const siteConfigSchema = z.object({
  siteName: z.string().min(1, 'Site name is required').max(255),
  siteDescription: z.string().max(1000).optional(),
  artistName: z.string().min(1, 'Artist name is required').max(255),
  artistBio: z.string().max(2000).optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().max(50).optional(),
  address: z.string().max(500).optional(),
  googleAnalyticsId: z.string().max(50).optional(),
  metaTitle: z.string().max(70).optional(),
  metaDescription: z.string().max(160).optional(),
  metaKeywords: z.string().max(500).optional(),
  ogImage: z.string().url().optional().or(z.literal('')),
  allowContactForm: z.boolean().default(true),
  showEmail: z.boolean().default(false),
  showPhone: z.boolean().default(false),
  showAddress: z.boolean().default(false),
  features: z
    .object({
      shop: z.boolean().default(true),
      journal: z.boolean().default(true),
      commissions: z.boolean().default(true),
      contact: z.boolean().default(true),
      newsletter: z.boolean().default(true),
      search: z.boolean().default(false),
    })
    .default({}),
})

type SiteConfigFormData = z.infer<typeof siteConfigSchema>

// =============================================================================
// Main Page Component
// =============================================================================

export function SettingsPage() {
  const navigate = useNavigate()
  const { data: config, isLoading } = useSiteConfig()
  const updateConfig = useUpdateSiteConfig()

  const form = useForm<SiteConfigFormData>({
    resolver: zodResolver(siteConfigSchema),
    defaultValues: {
      siteName: '',
      siteDescription: '',
      artistName: '',
      artistBio: '',
      email: '',
      phone: '',
      address: '',
      googleAnalyticsId: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      ogImage: '',
      allowContactForm: true,
      showEmail: false,
      showPhone: false,
      showAddress: false,
      features: { shop: true, journal: true, commissions: true, contact: true, newsletter: true, search: false },
    },
  })

  useEffect(() => {
    if (config) {
      form.reset({
        siteName: config.siteName || '',
        siteDescription: config.siteDescription || '',
        artistName: config.artistName || '',
        artistBio: config.artistBio || '',
        email: config.email || '',
        phone: config.phone || '',
        address: config.address || '',
        googleAnalyticsId: config.googleAnalyticsId || '',
        metaTitle: config.metaTitle || '',
        metaDescription: config.metaDescription || '',
        metaKeywords: config.metaKeywords || '',
        ogImage: config.ogImage || '',
        allowContactForm: config.allowContactForm ?? true,
        showEmail: config.showEmail ?? false,
        showPhone: config.showPhone ?? false,
        showAddress: config.showAddress ?? false,
        features: {
          shop: config.features?.shop ?? true,
          journal: config.features?.journal ?? true,
          commissions: config.features?.commissions ?? true,
          contact: config.features?.contact ?? true,
          newsletter: config.features?.newsletter ?? true,
          search: config.features?.search ?? false,
        },
      })
    }
  }, [config, form])

  const onSubmit = async (data: SiteConfigFormData) => {
    await updateConfig.mutateAsync(data)
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">
              Configure your portfolio site settings
            </p>
          </div>
        </div>
        <Button onClick={form.handleSubmit(onSubmit)} disabled={updateConfig.isPending}>
          {updateConfig.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Tabs defaultValue="general" className="space-y-6">
            <TabsList>
              <TabsTrigger value="general">
                <Settings className="h-4 w-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger value="seo">
                <Search className="h-4 w-4 mr-2" />
                SEO
              </TabsTrigger>
              <TabsTrigger value="contact">
                <Mail className="h-4 w-4 mr-2" />
                Contact
              </TabsTrigger>
              <TabsTrigger value="features">
                Features
              </TabsTrigger>
              <TabsTrigger value="privacy">
                <Shield className="h-4 w-4 mr-2" />
                Privacy
              </TabsTrigger>
            </TabsList>

            {/* General Settings */}
            <TabsContent value="general">
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>
                    Basic information about your portfolio site
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="rounded-md border border-border/60 bg-muted/40 p-4">
                    <p className="text-sm font-medium">Bio and description live in Site Content</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your biography and site description are edited under{' '}
                      <Link to="/app/easel/site-content" className="underline underline-offset-2">
                        Site Content
                      </Link>
                      , which is what your public site actually reads. Fields here used to save to a
                      different place that never reached the site.
                    </p>
                  </div>

                  <FormField
                    control={form.control}
                    name="siteName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Site Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="My Artist Portfolio" />
                        </FormControl>
                        <FormDescription>
                          The name of your portfolio website
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  

                  <Separator />

                  <FormField
                    control={form.control}
                    name="artistName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Artist Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="John Doe" />
                        </FormControl>
                        <FormDescription>
                          Your name as displayed on the site
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  

                  <Separator />

                  <FormField
                    control={form.control}
                    name="googleAnalyticsId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Google Analytics ID</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="G-XXXXXXXXXX" />
                        </FormControl>
                        <FormDescription>
                          Your Google Analytics tracking ID (optional)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* SEO Settings */}
            <TabsContent value="seo">
              <Card>
                <CardHeader>
                  <CardTitle>SEO Settings</CardTitle>
                  <CardDescription>
                    Optimize your site for search engines
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="metaTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="My Artist Portfolio | Fine Art" />
                        </FormControl>
                        <FormDescription>
                          The title shown in search results (max 70 characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="metaDescription"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Discover original artworks and prints by..."
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Description shown in search results (max 160 characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="metaKeywords"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Keywords</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="art, paintings, fine art, prints" />
                        </FormControl>
                        <FormDescription>
                          Comma-separated keywords for search engines
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ogImage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Share Image</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/og-image.jpg" />
                        </FormControl>
                        <FormDescription>
                          Image shown when your site is shared on social media
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contact Settings */}
            <TabsContent value="contact">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                  <CardDescription>
                    How visitors can reach you
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email Address</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="artist@example.com" />
                        </FormControl>
                        <FormDescription>
                          Your contact email address
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+1 (555) 123-4567" />
                        </FormControl>
                        <FormDescription>
                          Your contact phone number (optional)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="123 Art Street&#10;City, State 12345"
                            rows={3}
                          />
                        </FormControl>
                        <FormDescription>
                          Your studio or business address (optional)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="allowContactForm"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Contact Form</FormLabel>
                          <FormDescription>
                            Allow visitors to send you messages through a contact form
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Feature Toggles */}
            <TabsContent value="features">
              <Card>
                <CardHeader>
                  <CardTitle>Site Features</CardTitle>
                  <CardDescription>
                    Turn sections of your site on or off. Disabled features are hidden from your portfolio.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {([
                    ['features.shop', 'Shop', 'Show your shop — prints and products'],
                    ['features.journal', 'Journal', 'Publish studio notes, stories and updates'],
                    ['features.commissions', 'Commissions', 'Let visitors request commissioned work'],
                    ['features.contact', 'Contact', 'Show a contact form and inquiry intake'],
                    ['features.newsletter', 'Newsletter', 'Collect emails for your collector list'],
                    ['features.search', 'Search', 'Enable search across your portfolio'],
                  ] as const).map(([name, label, description]) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel>{label}</FormLabel>
                            <FormDescription>{description}</FormDescription>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Privacy Settings */}
            <TabsContent value="privacy">
              <Card>
                <CardHeader>
                  <CardTitle>Privacy Settings</CardTitle>
                  <CardDescription>
                    Control what information is visible on your portfolio
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="showEmail"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Show Email Address</FormLabel>
                          <FormDescription>
                            Display your email address publicly on your portfolio
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="showPhone"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Show Phone Number</FormLabel>
                          <FormDescription>
                            Display your phone number publicly on your portfolio
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="showAddress"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel>Show Address</FormLabel>
                          <FormDescription>
                            Display your address publicly on your portfolio
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </form>
      </Form>

      {/* Connecting an AI assistant is an account-level decision, so it lives with the
          other settings rather than buried in a developer section — the artist who wants
          it is not necessarily the artist who would find a developer tab. */}
      <div className="mt-6">
        <AgentTokensCard />
      </div>
    </div>
  )
}
